import React, { useState } from 'react';
import { X, CheckCircle, Send, AlertCircle } from 'lucide-react';
import { approveWithdrawalRequest, markAsTransferred } from '../lib/admin';
import AdminTransactionBreakdown from './AdminTransactionBreakdown';
import type { AdminWithdrawalRequest } from '../types/admin';

interface Props {
  request: AdminWithdrawalRequest;
  onClose: () => void;
  onUpdate: () => void;
}

export default function AdminWithdrawalModal({ request, onClose, onUpdate }: Props) {
  const [action, setAction] = useState<'approve' | 'transfer' | null>(null);
  const [transferReference, setTransferReference] = useState('');
  const [adminNotes, setAdminNotes] = useState(request.admin_notes || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    try {
      setLoading(true);
      setError('');
      await approveWithdrawalRequest(request.request_id, adminNotes);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkTransferred = async () => {
    if (!transferReference.trim()) {
      setError('Transfer reference is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await markAsTransferred(request.request_id, transferReference, adminNotes);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error marking as transferred:', err);
      setError('Failed to mark as transferred');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-900/50 text-yellow-200';
      case 'approved':
        return 'bg-blue-900/50 text-blue-200';
      case 'transferred':
        return 'bg-green-900/50 text-green-200';
      default:
        return 'bg-gray-900/50 text-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Withdrawal Request Details</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-6">
          {/* Request Information */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-white">Request Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Request ID</p>
                <p className="text-white font-mono">{request.request_id}</p>
              </div>
              <div>
                <p className="text-gray-400">Amount</p>
                <p className="text-white font-medium">LKR {request.amount.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-400">Status</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-gray-400">Requested Date</p>
                <p className="text-white">{new Date(request.requested_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Registry & Owner Information */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-white">Registry & Owner Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Registry Title</p>
                <p className="text-white">{request.registry_title}</p>
              </div>
              <div>
                <p className="text-gray-400">Owner Name</p>
                <p className="text-white">{request.owner_name}</p>
              </div>
              <div>
                <p className="text-gray-400">Email</p>
                <p className="text-white">{request.owner_email}</p>
              </div>
              <div>
                <p className="text-gray-400">Phone</p>
                <p className="text-white">{request.owner_phone || 'Not provided'}</p>
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-white">Bank Details for Transfer</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Account Holder Name</p>
                <p className="text-white font-medium">{request.account_holder_name || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-gray-400">Account Number</p>
                <p className="text-white font-mono">{request.account_number || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-gray-400">Bank Name</p>
                <p className="text-white">{request.bank_name || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-gray-400">Bank Branch</p>
                <p className="text-white">{request.bank_branch_name || 'Not provided'}</p>
              </div>
            </div>
          </div>

          {/* Transaction Breakdown */}
          <AdminTransactionBreakdown 
            originalAmount={request.amount} 
            currency="LKR"
          />

          {/* Transfer Reference (if transferred) */}
          {request.transfer_reference && (
            <div className="bg-gray-900/50 p-4 rounded-lg">
              <h4 className="font-medium text-white mb-2">Transfer Reference</h4>
              <p className="text-white font-mono">{request.transfer_reference}</p>
            </div>
          )}

          {/* Admin Notes */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Admin Notes
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
              rows={3}
              placeholder="Add notes about this request..."
              disabled={loading}
            />
          </div>

          {/* Transfer Reference Input (for transfer action) */}
          {action === 'transfer' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Transfer Reference Number *
              </label>
              <input
                type="text"
                value={transferReference}
                onChange={(e) => setTransferReference(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                placeholder="Enter bank transfer reference number"
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>

            {request.status === 'pending' && (
              <>
                {action === 'approve' ? (
                  <button
                    onClick={handleApprove}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{loading ? 'Approving...' : 'Confirm Approval'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setAction('approve')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                )}
              </>
            )}

            {(request.status === 'approved' || request.status === 'pending') && (
              <>
                {action === 'transfer' ? (
                  <button
                    onClick={handleMarkTransferred}
                    disabled={loading || !transferReference.trim()}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    <span>{loading ? 'Processing...' : 'Mark as Transferred'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setAction('transfer')}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    <Send className="w-4 h-4" />
                    <span>Mark as Transferred</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}